<template>
  <div
    v-if="!userFetchError || userLoading"
    class="sm-header-profile-menu align-center hide-lt-tablet"
  >
    <v-menu
      location="bottom left"
      width="300"
    >
      <template #activator="{ props, isActive }">
        <div
          class="d-flex align-center activator-holder"
          v-bind="props"
          @click="trackToggleMenu('avatar')"
        >
          <SMAvatar
            self
            data-cy="user-avatar-toggle-image"
            class="user-avatar-toggle-image"
            :size="36"
            :show-loading="loading"
          />
          <v-icon class="pt-4 text-secondary">
            {{ isActive ? 'ico-sm-arrow-up' : 'ico-sm-arrow-down' }}
          </v-icon>
        </div>
      </template>
      <v-list>
        <v-list-item
          :data-cy="'headerMenu-profile'"
          class="pl-6"
          @click="viewProfileClick"
        >
          <div class="align-start mr-4">
            <SMAvatar
              self
              class="user-avatar-small"
              :size="24"
              :show-loading="loading"
            />
          </div>
          <div>
            <v-list-item-title class="font-weight-medium">
              {{ fullname }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-body-1">
              {{ $t('global.view-profile') }}
            </v-list-item-subtitle>
          </div>
        </v-list-item>
        <div
          v-if="userAvatarIsGenerated"
          class="full-width pl-6 pr-4 my-4"
        >
          <input
            ref="image"
            class="d-none"
            type="file"
            accept="image/png,image/jpg,image/jpeg"
            @change="onFilePicked"
          >
          <v-btn
            elevation="0"
            color="#432CA014"
            class="full-width text-capitalize text-body-1 font-weight-bold profile-btn"
            @click="addProfilePictureClick"
          >
            {{ $t('global.profile-menu.add-profile-picture.btn') }}
          </v-btn>
        </div>
        <v-divider />
        <v-list-item
          v-for="(item, i) in menuItems"
          v-show="!item.hidden"
          :key="i"
          :data-cy="item.name"
          class="pl-6"
          @click="item.event"
        >
          <div
            v-if="item.hasAvatar"
            class="align-start mr-4"
          >
            <SMAvatar
              self
              class="user-avatar-small"
              :size="24"
            />
          </div>
          <div
            v-if="item.icon"
            class="mr-4"
          >
            <SMWhatsNewBadge
              v-if="item.hasBadge"
              :offset="[12, 0]"
              overlap
            >
              <v-icon class="text-secondary">
                {{ item.icon }}
              </v-icon>
            </SMWhatsNewBadge>
            <v-icon
              v-else
              class="text-secondary"
            >
              {{ item.icon }}
            </v-icon>
          </div>
          <div>
            <div
              v-if="item.title"
              :class="{'font-weight-medium': item.name === 'headerMenu-profile'}"
            >
              {{ item.title }}
            </div>
            <v-list-item-subtitle
              v-if="item.subtitle"
              :class="{'text-body-1': item.name === 'headerMenu-profile'}"
            >
              {{ item.subtitle }}
            </v-list-item-subtitle>
          </div>
        </v-list-item>
        <v-divider />
        <v-list-item
          :data-cy="logoutItem.name"
          class="pl-6"
          @click="logoutItem.event"
        >
          <div class="mr-4">
            <v-icon class="text-secondary">
              {{ logoutItem.icon }}
            </v-icon>
          </div>
          <div>{{ logoutItem.title }}</div>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import SMAvatar from '@src/ui/SMAvatar/index.vue'
import SMWhatsNewBadge from '@src/ui/SMWhatsNew/components/SMWhatsNewBadge/index.vue'
import { logout } from '@src/utilities/auth'
import useSocialDialogTracking from '@src/ui/SMSocialDialog/composables/useSocialDialogTracking'
import { useInviteDialogStore } from '@src/pinia/inviteDialog'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import useTracking from '@src/composables/tracking/tracking'
import { useI18n } from '@src/composables/useI18n'
import useRealmSettings from '@src/composables/realmSettings'
import useUser from '@src/composables/user'
import { useUserGuideSidenavStore } from '@src/pinia/userGuideSidenav'
import { computedAsync } from '@vueuse/core'
import { userGraphQlApi } from '@src/api/apiModuleInstances'
import useSnackbar from '@src/composables/snackbar'
import { useInstallOverlay } from '@src/ui/SMInstallOverlay/composables/useInstallOverlay'

interface IMenuItem {
  title: string;
  event: () => void;
  target: string;
  subtitle?: string;
  hasAvatar?: boolean;
  hasBadge?: boolean;
  icon?: string;
  hidden?: boolean;
  name: string; // required for integration tests to select item un-ambiguously
}

export default defineComponent({
  name: 'SMHeaderProfileMenu',
  components: {
    SMAvatar,
    SMWhatsNewBadge
  },
  setup() {
    const { clickInvite } = useSocialDialogTracking()
    const { trackMixpanelEvent } = useTracking()
    const { isOpen } = storeToRefs(useUserGuideSidenavStore())
    const { realmSettings } = useRealmSettings()
    const {
      fullname, userLoading, userFetchError, user, imageError, setUserImage
    } = useUser()
    const { t } = useI18n()
    const { isInviteDialogOpen } = storeToRefs(useInviteDialogStore())
    const router = useRouter()
    const { showSnackbar } = useSnackbar()
    const { openInstallOverlay } = useInstallOverlay()

    const networkName = computed(() => realmSettings.value?.metadata.realm_name ?? '')

    const loading = ref(false)

    const userAvatarIsGenerated = computedAsync<boolean>(async () => {
      if (user.value.global_user_id) {
        const userAvatarIsGeneratedValue = await userGraphQlApi.getUserAvatarIsGenerated(user.value.global_user_id)
        return userAvatarIsGeneratedValue
      }
      return false
    }, undefined)

    function trackInviteClick() {
      clickInvite({ component: 'navigation_menu' })
    }

    function viewProfileClick() {
      trackMixpanelEvent('clickUserNavigationItem', {
        component: 'navigation_menu',
        element: 'menu_item',
        type: 'click'
      })
      router.push({ path: '/user/' })
    }

    function settingsOverviewClick() {
      trackMixpanelEvent('clickSettings', {
        component: 'navigation_menu',
        element: 'menu_item',
        type: 'click'
      })
      router.push({ path: '/settings/' })
    }
    function inviteClick() {
      isInviteDialogOpen.value = true
      trackInviteClick()
    }
    function signOutClick() {
      trackMixpanelEvent('signOut', {
        component: 'navigation_menu',
        element: 'menu_item',
        type: 'click'
      })
      logout()
    }
    function trackToggleMenu(element: 'avatar'| 'icon'): void {
      trackMixpanelEvent('clickUserNavigationMenu', {
        component: 'navigation_menu',
        element,
        type: 'click'
      })
    }

    function openDrawer() {
      isOpen.value = true
    }

    const menuItems = computed<IMenuItem[]>(() => {
      return [
        {
          title: t('global.settings').toString(),
          event: settingsOverviewClick,
          target: '_self',
          icon: 'ico-sm-settings-line',
          name: 'headerMenu-settings'
        },
        {
          title: t('user-guide.header.tooltip.title').toString(),
          event: openDrawer,
          target: '_self',
          icon: 'ico-sm-user-guide-line',
          name: 'headerMenu-user-guide',
          hasBadge: true
        },
        {
          title: t('install.menu.button', { name: networkName.value }).toString(),
          event: openInstallOverlay,
          target: '_self',
          icon: 'ico-sm-download',
          name: 'headerMenu-install'
        },
        {
          title: t('global.invite').toString(),
          event: inviteClick,
          target: '_self',
          icon: 'ico-sm-recommend-line',
          hidden: !realmSettings.value?.user_admin.invitation_enabled,
          name: 'headerMenu-invite'
        }
      ]
    })

    const logoutItem = computed<IMenuItem>(() => ({
      title: t('dashboard.header.menu.logout').toString(),
      event: signOutClick,
      target: '_self',
      icon: 'ico-sm-sign-out',
      name: 'headerMenu-logout'
    }))

    const image = ref<HTMLInputElement | null>(null)

    function pickFile(): void {
      (image.value as HTMLInputElement).click()
    }

    function addProfilePictureClick(): void {
      trackMixpanelEvent('clickAddProfilePicture', {
        component: 'navigation_menu',
        element: 'button',
        type: 'click'
      })
      pickFile()
    }

    function onFilePicked(e: Event): void {
      loading.value = true
      const { files } = e.target as HTMLInputElement
      if (files && files.length) {
        const fr = new FileReader()
        const file = files[0]
        fr.readAsDataURL(file)
        fr.addEventListener('load', async () => {
          await setUserImage(file)
          if (imageError.value) {
            showSnackbar('error.undefined')
          } else {
            showSnackbar('global.profile-menu.add-profile-picture.success')
          }
          loading.value = false
        })
      }
    }

    return {
      t,
      isInviteDialogOpen,
      menuItems,
      logoutItem,
      userLoading,
      userFetchError,
      userAvatarIsGenerated,
      fullname,
      image,
      loading,
      pickFile,
      onFilePicked,
      viewProfileClick,
      addProfilePictureClick,
      trackToggleMenu
    }
  }
})
</script>

<style lang="scss" scoped>
.sm-header-profile-menu{
  .activator-holder{
    cursor: pointer;
  }
}
.profile-btn {
  :deep(.v-btn__content) {
    color: #432CA0 !important;
  }
}
</style>
